import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatMenuTrigger } from '@angular/material/menu';
import { take } from 'rxjs';

import { TPropertySubType } from '@shared/models/properties';
import { IPropertyType, PROPERTY_TYPES } from '@core/constants/properties';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { setUnsavedSubtypeFiltersAction } from '@state/filters/filters.actions';
import { selectFiltersCurrent } from '@state/filters/filters.selectors';

@UntilDestroy()
@Component({
  selector: 'app-filter-v2-subtype',
  templateUrl: './filter-v2-subtype.component.html',
  styleUrls: [
    './filter-v2-subtype.component.scss',
    '../filters-v2.component.scss',
  ],
})
export class FilterV2SubtypeComponent implements OnInit {
  @Input() mobile = false;
  @Output() applyFilters = new EventEmitter();
  @ViewChild('trigger') menuTrigger!: MatMenuTrigger;

  protected isOpen = false;
  protected allSelected = false;
  protected propertyTypes: IPropertyType[] = PROPERTY_TYPES;
  protected checkedCount = 0;
  protected totalSubtypes = PROPERTY_TYPES.reduce(
    (acc, curr) => acc + curr.subtypes.length,
    0
  );
  public checkedSubtypes: { [key: string]: boolean } = PROPERTY_TYPES.reduce(
    (acc, curr) => ({
      ...acc,
      ...curr.subtypes.reduce(
        (acc2, curr2) => ({ ...acc2, [curr2]: false }),
        {}
      ),
    }),
    {}
  );

  constructor(private store: Store<AppState>) {}

  ngOnInit() {
    // for loading the first time
    this.store
      .select(selectFiltersCurrent)
      .pipe(take(1))
      .subscribe((filters) => {
        if (filters?.subType && filters.subType.in) {
          this.checkedCount = filters.subType.in.length;
          if (this.checkedCount === this.totalSubtypes) this.allSelected = true;
          this.checkedSubtypes = this.propertyTypes.reduce(
            (acc, curr) => ({
              ...acc,
              ...curr.subtypes.reduce(
                (acc2, curr2) => ({
                  ...acc2,
                  [curr2]: filters?.subType?.in?.includes(
                    curr2 as TPropertySubType
                  ),
                }),
                {}
              ),
            }),
            {}
          );
        }
      });

    // for clearing the filters
    this.store
      .select(selectFiltersCurrent)
      .pipe(untilDestroyed(this))
      .subscribe((filters) => {
        if (!filters?.subType?.in && this.checkedCount > 0) {
          this.checkedCount = 0;
          this.allSelected = false;
          this.checkedSubtypes = PROPERTY_TYPES.reduce(
            (acc, curr) => ({
              ...acc,
              ...curr.subtypes.reduce(
                (acc2, curr2) => ({ ...acc2, [curr2]: false }),
                {}
              ),
            }),
            {}
          );
        }
      });
  }

  onChangeSubtypes() {
    const subtypesLength = Object.values(this.checkedSubtypes).filter(
      (subtype) => subtype
    ).length;
    this.checkedCount = subtypesLength;
    if (subtypesLength) {
      this.store.dispatch(
        setUnsavedSubtypeFiltersAction({
          in: [
            ...Object.keys(this.checkedSubtypes).filter(
              (key) => this.checkedSubtypes[key]
            ),
          ] as TPropertySubType[],
        })
      );
    } else {
      this.store.dispatch(setUnsavedSubtypeFiltersAction({}));
    }
    if (!this.mobile) this.applyFilters.emit();
  }

  toggleAllSelection(): void {
    this.allSelected = !this.allSelected;
    this.checkedSubtypes = PROPERTY_TYPES.reduce(
      (acc, curr) => ({
        ...acc,
        ...curr.subtypes.reduce(
          (acc2, curr2) => ({ ...acc2, [curr2]: this.allSelected }),
          {}
        ),
      }),
      {}
    );
    this.onChangeSubtypes();
  }

  resetSubtypes() {
    this.checkedCount = 0;
    this.allSelected = false;
    this.checkedSubtypes = PROPERTY_TYPES.reduce(
      (acc, curr) => ({
        ...acc,
        ...curr.subtypes.reduce(
          (acc2, curr2) => ({ ...acc2, [curr2]: false }),
          {}
        ),
      }),
      {}
    );
    this.store.dispatch(setUnsavedSubtypeFiltersAction({}));
    this.applyFilters.emit();
  }

  closeMenu() {
    this.menuTrigger.closeMenu();
  }

  loadInitialValue() {
    this.store
      .select(selectFiltersCurrent)
      .pipe(take(1))
      .subscribe((filters) => {
        if (filters?.subType?.in) {
          this.checkedCount = filters.subType.in.length;
          if (this.checkedCount === this.totalSubtypes) this.allSelected = true;
          this.checkedSubtypes = this.propertyTypes.reduce(
            (acc, curr) => ({
              ...acc,
              ...curr.subtypes.reduce(
                (acc2, curr2) => ({
                  ...acc2,
                  [curr2]: filters?.subType?.in?.includes(
                    curr2 as TPropertySubType
                  ),
                }),
                {}
              ),
            }),
            {}
          );
        } else {
          this.checkedCount = 0;
          this.allSelected = false;
          this.checkedSubtypes = PROPERTY_TYPES.reduce(
            (acc, curr) => ({
              ...acc,
              ...curr.subtypes.reduce(
                (acc2, curr2) => ({ ...acc2, [curr2]: false }),
                {}
              ),
            }),
            {}
          );
        }
      });
  }
}
